import { useContext } from "react";
import { Navigate, useMatch } from "react-router-dom";
import { FCCC } from "types";

import { DataContext } from "@/provider/DataContextProvider";

/**
 * This component guards Steps in the Onboarding so that the user cannot jump from Step 1 to Step 5.
 *
 * Each Onboarding step writes the **next allowed** step into the `dataContext`.
 * This component then allows the display and the Onboarding step navigates there.
 *
 * The component is used to protect the steps in the initial onboarding (e.g. /onboarding/1/sim-selection -> /onboarding/2/rate-selection)
 * and in the portal for every additional sim. This is also why the `basePath` is necessary.
 *
 * Fresh flows get `allowedMaxOnboardingStep: 1` from {@link AddDevice} on entry.
 * `undefined` means onboarding is complete and the guard is off.
 *
 * @param basePath - The basePath used for matching the onboarding location
 **/
export const StepGuard: FCCC<{ basePath: string }> = ({
  basePath,
  children,
}) => {
  const { dataContext } = useContext(DataContext);
  const parameters = useMatch(`${basePath}/:step/:substep`);
  const step = parameters?.params.step
    ? Number.parseInt(parameters.params.step)
    : 1;

  if (
    // If `allowedMaxOnboardingStep` is undefined, we should not navigate, as the onboarding is completed.
    dataContext?.allowedMaxOnboardingStep !== undefined &&
    dataContext.allowedMaxOnboardingStep < step
  ) {
    return (
      <Navigate
        to={`${basePath}/${dataContext.allowedMaxOnboardingStep}`}
        replace
      />
    );
  } else {
    return <>{children}</>;
  }
};
